import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import SpaIcon from '@mui/icons-material/Spa';
import WbSunnyIcon from '@mui/icons-material/WbSunny';

const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';

const PreferencesManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedPreference, setSelectedPreference] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'mealType',
    name: '',
    value: '',
    description: '',
    icon: '',
    displayOrder: 0
  });
  
  const preferenceTypes = [
    { value: 'mealType', label: 'Meal Type', tabIndex: 0 },
    { value: 'dietaryType', label: 'Dietary Type', tabIndex: 1 },
    { value: 'cuisineStyle', label: 'Cuisine Style', tabIndex: 2 },
    { value: 'season', label: 'Season', tabIndex: 3 }
  ];
  
  const iconOptions = [
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'breakfast_dining', label: 'Breakfast' },
    { value: 'lunch_dining', label: 'Lunch' },
    { value: 'dinner_dining', label: 'Dinner' },
    { value: 'local_pizza', label: 'Pizza' },
    { value: 'spa', label: 'Vegetarian' },
    { value: 'eco', label: 'Eco/Vegan' },
    { value: 'wb_sunny', label: 'Sun/Summer' },
    { value: 'ac_unit', label: 'Winter' },
    { value: 'egg', label: 'Egg' },
    { value: 'coffee', label: 'Coffee' },
    { value: 'ramen_dining', label: 'Ramen' },
    { value: 'kebab_dining', label: 'Kebab' },
    { value: 'grass', label: 'Grass' },
    { value: 'favorite', label: 'Favorite' },
    { value: 'umbrella', label: 'Umbrella/Rain' },
    { value: 'forest', label: 'Forest/Autumn' }
  ];
  
  // Get token from Redux store for authentication
  const token = localStorage.getItem('token');
  
  // Set up headers for API requests with authentication
  const headers = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  
  // Fetch all preferences from the API
  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/preferences`, headers);
      setPreferences(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError('Failed to load preferences. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load preferences when component mounts
  useEffect(() => {
    fetchPreferences();
  }, []);
  
  // Filter preferences by type based on selected tab
  const filteredPreferences = preferences.filter(pref => {
    switch(tabValue) {
      case 0: return pref.type === 'mealType';
      case 1: return pref.type === 'dietaryType';
      case 2: return pref.type === 'cuisineStyle';
      case 3: return pref.type === 'season';
      default: return true;
    }
  }).sort((a, b) => a.displayOrder - b.displayOrder);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Open dialog for adding new preference
  const handleOpenAddDialog = () => {
    const type = tabValue === 0 ? 'mealType' :
               tabValue === 1 ? 'dietaryType' :
               tabValue === 2 ? 'cuisineStyle' : 'season';
    
    setFormData({
      type,
      name: '',
      value: '',
      description: '',
      icon: '',
      displayOrder: preferences.filter(p => p.type === type).length + 1
    });
    
    setDialogMode('add');
    setOpenDialog(true);
  };
  
  // Open dialog for editing preference
  const handleOpenEditDialog = (preference) => {
    setSelectedPreference(preference);
    setFormData({
      type: preference.type,
      name: preference.name,
      value: preference.value,
      description: preference.description || '',
      icon: preference.icon || '',
      displayOrder: preference.displayOrder || 0
    });
    
    setDialogMode('edit');
    setOpenDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPreference(null);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Auto-generate value from name if it's empty
    if (name === 'name' && !formData.value) {
      setFormData(prev => ({
        ...prev,
        value: value
      }));
    }
  };
  
  // Submit form to create or update preference
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'add') {
        // Create new preference
        const response = await axios.post(`${API_URL}/preferences`, formData, headers);
        setPreferences([...preferences, response.data]);
        setSnackbar({
          open: true,
          message: `${formData.name} has been added successfully!`,
          severity: 'success'
        });
      } else {
        // Update existing preference
        const response = await axios.put(
          `${API_URL}/preferences/${selectedPreference._id}`,
          formData,
          headers
        );
        
        setPreferences(preferences.map(pref => 
          pref._id === selectedPreference._id ? response.data : pref
        ));
        
        setSnackbar({
          open: true,
          message: `${formData.name} has been updated successfully!`,
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving preference:', err);
      setSnackbar({
        open: true,
        message: `Error: ${err.response?.data?.message || 'Failed to save. Please try again.'}`,
        severity: 'error'
      });
    }
  };
  
  // Delete preference
  const handleDelete = async (preference) => {
    if (window.confirm(`Are you sure you want to delete ${preference.name}?`)) {
      try {
        await axios.delete(`${API_URL}/preferences/${preference._id}`, headers);
        setPreferences(preferences.filter(pref => pref._id !== preference._id));
        
        setSnackbar({
          open: true,
          message: `${preference.name} has been deleted successfully!`,
          severity: 'success'
        });
      } catch (err) {
        console.error('Error deleting preference:', err);
        setSnackbar({
          open: true,
          message: `Error: ${err.response?.data?.message || 'Failed to delete. Please try again.'}`,
          severity: 'error'
        });
      }
    }
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Get icon for preference type
  const getTypeIcon = (type) => {
    switch(type) {
      case 'mealType': return <LocalDiningIcon />;
      case 'dietaryType': return <SpaIcon />;
      case 'cuisineStyle': return <RestaurantIcon />;
      case 'season': return <WbSunnyIcon />;
      default: return null;
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Manage Preferences
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Add, edit, or remove meal types, dietary preferences, cuisine styles, and seasons.
        These will be available throughout the application for users to select from.
      </Typography>
      
      <Paper sx={{ mt: 3, mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Meal Types" icon={<LocalDiningIcon />} iconPosition="start" />
          <Tab label="Dietary Types" icon={<SpaIcon />} iconPosition="start" />
          <Tab label="Cuisine Styles" icon={<RestaurantIcon />} iconPosition="start" />
          <Tab label="Seasons" icon={<WbSunnyIcon />} iconPosition="start" />
        </Tabs>
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {tabValue === 0 ? 'Meal Types' : 
               tabValue === 1 ? 'Dietary Types' : 
               tabValue === 2 ? 'Cuisine Styles' : 'Seasons'} ({filteredPreferences.length})
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Add New
            </Button>
          </Box>
          
          <Paper sx={{ mb: 4 }}>
            <List>
              {filteredPreferences.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No items found. Add your first one!" />
                </ListItem>
              ) : (
                filteredPreferences.map((pref, index) => (
                  <React.Fragment key={pref._id}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" fontWeight="medium">
                              {pref.name}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={pref.value} 
                              sx={{ ml: 1 }} 
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={pref.description}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton edge="end" onClick={() => handleOpenEditDialog(pref)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton edge="end" onClick={() => handleDelete(pref)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New' : 'Edit'} {' '}
          {tabValue === 0 ? 'Meal Type' : 
           tabValue === 1 ? 'Dietary Type' : 
           tabValue === 2 ? 'Cuisine Style' : 'Season'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  name="type"
                  value={formData.type}
                  label="Type"
                  onChange={handleInputChange}
                >
                  {preferenceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                helperText="Display name shown to users"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Value"
                name="value"
                value={formData.value}
                onChange={handleInputChange}
                required
                helperText="Unique identifier (lowercase, hyphenated)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
                helperText="Optional description"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="icon-label">Icon</InputLabel>
                <Select
                  labelId="icon-label"
                  name="icon"
                  value={formData.icon}
                  label="Icon"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {iconOptions.map(icon => (
                    <MenuItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                name="displayOrder"
                type="number"
                value={formData.displayOrder}
                onChange={handleInputChange}
                helperText="Order in lists (smaller numbers first)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {dialogMode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PreferencesManager;
